import DocumentMeta from "react-document-meta";
import ExampleLandingHeader from "../components/ExampleLandingHeader";
import { useTranslation } from "../i18n/LanguageContext";
import FloatingWhatsAppButton from "../components/FloatingWhatsAppButton";
import SocialLinksSection from "../components/SocialLinksSection";
import { buildNoIndexMeta } from "../seo";

const copy = {
    it: {
        title: "Ferragosto in terrazza",
        metaTitle: "Promo Ferragosto | Cena e musica dal vivo",
        metaDescription: "Pacchetto speciale di Ferragosto con cena e musica. Posti limitati, prenota subito.",
        intro: "Cena completa, musica dal vivo e brindisi di mezzanotte. Un’offerta speciale solo per il 15 agosto.",
        menu: [
            { label: "Promo", href: "#promo" },
            { label: "Prenota", href: "#prenota" },
            { label: "Info", href: "#info" }
        ],
        ctaPrimary: "Scopri la promo",
        ctaSecondary: "Prenota ora",
        priceLabel: "Solo per una notte",
        priceValue: "€65 a persona",
        priceNote: "Cena + musica live + brindisi",
        priceWarning: "Posti limitati, prenotazione obbligatoria.",
        includesTitle: "Cosa include",
        includes: [
            "Cena di 4 portate con vino incluso",
            "Musica dal vivo e area lounge",
            "Brindisi di mezzanotte vista mare"
        ],
        infoTitle: "Info rapide",
        infoItems: [
            "Inizio cena alle 20:30",
            "Parcheggio disponibile vicino alla terrazza",
            "Dress code: estivo elegante"
        ],
        bookTitle: "Prenota il tuo posto",
        bookSubtitle: "Posti limitati, riserva ora il tuo tavolo.",
        bookCta1: "Prenota su WhatsApp",
        bookCta2: "Chiama ora"
    },
    en: {
        title: "Ferragosto on the terrace",
        metaTitle: "Ferragosto offer | Dinner and live music",
        metaDescription: "Special Ferragosto package with dinner and music. Limited seats, book now.",
        intro: "Full dinner, live music and a midnight toast. A special offer only for August 15.",
        menu: [
            { label: "Offer", href: "#promo" },
            { label: "Book", href: "#prenota" },
            { label: "Info", href: "#info" }
        ],
        ctaPrimary: "See the offer",
        ctaSecondary: "Book now",
        priceLabel: "One night only",
        priceValue: "€65 per person",
        priceNote: "Dinner + live music + toast",
        priceWarning: "Limited seats, booking required.",
        includesTitle: "What’s included",
        includes: [
            "4‑course dinner with wine",
            "Live music and lounge area",
            "Midnight toast with sea view"
        ],
        infoTitle: "Quick info",
        infoItems: [
            "Dinner starts at 8:30 pm",
            "Parking near the terrace",
            "Dress code: smart summer"
        ],
        bookTitle: "Book your seat",
        bookSubtitle: "Limited seats, reserve your table now.",
        bookCta1: "Book on WhatsApp",
        bookCta2: "Call now"
    },
    de: {
        title: "Ferragosto auf der Terrasse",
        metaTitle: "Ferragosto‑Angebot | Dinner und Live‑Musik",
        metaDescription: "Sonderpaket mit Dinner und Musik. Begrenzte Plätze, jetzt buchen.",
        intro: "Komplettes Dinner, Live‑Musik und Mitternachts‑Toast. Nur am 15. August.",
        menu: [
            { label: "Angebot", href: "#promo" },
            { label: "Buchen", href: "#prenota" },
            { label: "Info", href: "#info" }
        ],
        ctaPrimary: "Angebot ansehen",
        ctaSecondary: "Jetzt buchen",
        priceLabel: "Nur eine Nacht",
        priceValue: "€65 pro Person",
        priceNote: "Dinner + Live‑Musik + Toast",
        priceWarning: "Begrenzte Plätze, Reservierung erforderlich.",
        includesTitle: "Inklusive",
        includes: [
            "4‑Gänge‑Dinner mit Wein",
            "Live‑Musik und Lounge‑Bereich",
            "Mitternachts‑Toast mit Meerblick"
        ],
        infoTitle: "Kurzinfo",
        infoItems: [
            "Dinner ab 20:30",
            "Parkplatz in der Nähe",
            "Dresscode: sommerlich elegant"
        ],
        bookTitle: "Platz reservieren",
        bookSubtitle: "Begrenzte Plätze, jetzt reservieren.",
        bookCta1: "Per WhatsApp buchen",
        bookCta2: "Jetzt anrufen"
    }
};

const FerragostoPromoPage = () => {
    const { language } = useTranslation();
    const t = copy[language] || copy.it;

    const meta = buildNoIndexMeta({
        title: t.metaTitle,
        description: t.metaDescription,
        path: "/estate-2026/promo-ferragosto"
    });

    return (
        <>
            <DocumentMeta {...meta} />
            <div className="min-h-screen bg-gradient-to-b from-rose-200 via-white to-orange-100 text-rose-950">
                <header className="px-6 pt-10 pb-10 md:px-12 lg:px-20">
                    <ExampleLandingHeader title={t.title} items={t.menu} />
                    <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_0.9fr]">
                        <div>
                            <p className="text-lg text-rose-800">{t.intro}</p>
                            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                                <a href="#promo" className="rounded-full bg-rose-600 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-rose-200">
                                    {t.ctaPrimary}
                                </a>
                                <a href="#prenota" className="rounded-full border border-rose-600 px-6 py-3 text-sm font-semibold text-rose-700">
                                    {t.ctaSecondary}
                                </a>
                            </div>
                        </div>
                        <div className="rounded-3xl bg-white p-6 shadow-lg">
                            <p className="text-sm uppercase tracking-widest text-rose-600">{t.priceLabel}</p>
                            <p className="mt-4 text-4xl font-semibold">{t.priceValue}</p>
                            <p className="mt-2 text-sm text-rose-700">{t.priceNote}</p>
                            <div className="mt-6 rounded-2xl bg-rose-50 p-4">
                                <p className="text-sm">{t.priceWarning}</p>
                            </div>
                        </div>
                    </div>
                    <div className="mt-8 overflow-hidden rounded-3xl shadow-lg">
                        <img
                            src="https://images.unsplash.com/photo-1504674900247-0877df9cc836?q=80&w=1600&auto=format&fit=crop"
                            alt="Cena speciale di Ferragosto"
                            className="h-64 w-full object-cover md:h-96"
                            loading="lazy"
                        />
                    </div>
                </header>

                <main className="px-6 pb-24 md:px-12 lg:px-20">
                    <section id="promo" className="rounded-3xl bg-white p-6 shadow-lg md:p-10">
                        <h2 className="text-2xl font-semibold">{t.includesTitle}</h2>
                        <div className="mt-6 grid gap-4 md:grid-cols-3">
                            {t.includes.map((item) => (
                                <div key={item} className="rounded-2xl border border-rose-100 p-4">{item}</div>
                            ))}
                        </div>
                    </section>
                    <div className="mt-8 grid gap-4 md:grid-cols-2">
                        <img
                            src="https://images.unsplash.com/photo-1504674900247-0877df9cc836?q=80&w=1400&auto=format&fit=crop"
                            alt="Tavola apparecchiata per cena estiva"
                            className="h-56 w-full rounded-3xl object-cover md:h-72"
                            loading="lazy"
                        />
                        <img
                            src="https://images.unsplash.com/photo-1469474968028-56623f02e42e?q=80&w=1400&auto=format&fit=crop"
                            alt="Fuochi d'artificio estivi"
                            className="h-56 w-full rounded-3xl object-cover md:h-72"
                            loading="lazy"
                        />
                    </div>

                    <section id="info" className="mt-10 rounded-3xl bg-rose-50 p-6 shadow-lg md:p-10">
                        <h2 className="text-2xl font-semibold">{t.infoTitle}</h2>
                        <ul className="mt-4 space-y-3 text-rose-700">
                            {t.infoItems.map((item) => (
                                <li key={item}>{item}</li>
                            ))}
                        </ul>
                    </section>

                    <SocialLinksSection className="mt-10" />

                    <section id="prenota" className="mt-10 rounded-3xl bg-rose-700 p-6 text-white shadow-lg md:p-10">
                        <h2 className="text-2xl font-semibold">{t.bookTitle}</h2>
                        <p className="mt-2 text-rose-100">{t.bookSubtitle}</p>
                        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                            <a href="https://wa.me/393515699197" className="rounded-full bg-white px-6 py-3 text-sm font-semibold text-rose-700">
                                {t.bookCta1}
                            </a>
                            <a href="tel:+393515699197" className="rounded-full border border-white px-6 py-3 text-sm font-semibold text-white">
                                {t.bookCta2}
                            </a>
                        </div>
                    </section>
                </main>
                <FloatingWhatsAppButton />
            </div>
        </>
    );
};

export default FerragostoPromoPage;
